/**
 * Deliberate duplicate of `src/components/Profiles/ImageUploadField/ImageUploadField.tsx`.
 * The only difference is which api module it uploads through.
 *
 * The two are kept as separate components by decision D6 of the
 * `move-event-images-to-content-api` change: event images are owned by content-api
 * (`POST /api/v1/events/images`) while profile files stay on profiles-api
 * (`POST /api/v1/profiles/files`). Sharing one component would have meant editing a
 * production Profiles screen used by four other forms, and we would not put that inside an
 * events change's blast radius.
 *
 * **If you change one, check the other.**
 *
 * The two have since diverged in one place: this events copy gained the optional
 * `onUploadingChange` prop. `EventFormModal` has a Save button that would otherwise submit the
 * PREVIOUS image ref while a file was still in flight — the modal closes and the upload's
 * result attaches to nothing — so the events form has to know when an upload is running. The
 * Profiles copy has no such form and was deliberately left alone rather than pulled into an
 * events change's blast radius. Everything else is still line-for-line the same.
 */
import { Button, Icon } from "@legal-500/legal500-design-system";
import { useEffect, useRef, useState } from "react";
import { buildFilesUrl, eventFilesService, type ImageRef } from "@/api/events/files";
import styles from "./EventImageUploadField.module.css";

export type EventImageUploadFieldProps = {
  value: ImageRef | null;
  onChange: (ref: ImageRef | null) => void;
  label?: string;
  slug?: string;
  accept?: string;
  disabled?: boolean;
  /**
   * Fires whenever an upload starts or ends, so a parent form can refuse to save while a
   * file is still in flight. Optional: a caller that does not pass it is unaffected.
   */
  onUploadingChange?: (uploading: boolean) => void;
};

const IMAGE_EXT = /\.(png|jpe?g|gif|webp|svg)$/i;

export function EventImageUploadField({
  value,
  onChange,
  label,
  slug,
  accept = "image/png,image/jpeg,image/gif,image/webp",
  disabled = false,
  onUploadingChange,
}: EventImageUploadFieldProps) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  // Mirrors `uploading` for the unmount cleanup, which cannot read the state it closed over.
  const uploadingRef = useRef(false);
  // Callers pass inline lambdas, so the cleanup reads the latest one from a ref rather than
  // depending on it — an effect keyed on the callback would tear down on every render.
  const onUploadingChangeRef = useRef(onUploadingChange);
  onUploadingChangeRef.current = onUploadingChange;

  const previewUrl = buildFilesUrl(value, slug);
  const isImage = value ? IMAGE_EXT.test(value.filename) : false;

  /** The one way the flag moves: the parent hears about every start and every end. */
  const setUploadingState = (next: boolean) => {
    uploadingRef.current = next;
    setUploading(next);
    onUploadingChange?.(next);
  };

  // A field torn down mid-upload must not leave the parent's Save button disabled for good:
  // EventsListPage keys the modal on `editing?.id ?? "new"`, so closing and reopening the new
  // event form reuses the same EventFormModal instance and its `imageUploading` state.
  useEffect(
    () => () => {
      if (uploadingRef.current) onUploadingChangeRef.current?.(false);
    },
    [],
  );

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingState(true);
    setError(null);
    try {
      const { hash, filename } = await eventFilesService.upload(file);
      onChange({ hash, filename });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Upload failed");
    } finally {
      setUploadingState(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div className={styles.root}>
      {label && <span className={styles.label}>{label}</span>}

      {value ? (
        <div className={styles.selected}>
          {isImage && previewUrl ? (
            <img src={previewUrl} alt={value.filename} className={styles.thumb} />
          ) : (
            <div className={styles.fileThumb} aria-hidden="true">
              <Icon icon="external" size="default" />
            </div>
          )}
          <div className={styles.selectedMeta}>
            <p className={styles.selectedTitle}>{value.filename}</p>
            <p className={styles.selectedSubtitle}>Uploaded</p>
          </div>
          <div className={styles.selectedActions}>
            <Button
              type="button"
              variant="outlined"
              size="small"
              onClick={() => inputRef.current?.click()}
              disabled={disabled || uploading}
            >
              {uploading ? "Uploading…" : "Change"}
            </Button>
            <Button
              type="button"
              variant="icon"
              size="small"
              aria-label="Remove image"
              onClick={() => onChange(null)}
              disabled={disabled || uploading}
            >
              <Icon icon="cross" />
            </Button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          className={styles.trigger}
          onClick={() => inputRef.current?.click()}
          disabled={disabled || uploading}
        >
          <Icon icon="plus" size="small" />
          <span>{uploading ? "Uploading…" : "Upload image…"}</span>
        </button>
      )}

      {error && <p className={styles.error}>{error}</p>}

      <input
        ref={inputRef}
        type="file"
        accept={accept}
        hidden
        aria-hidden="true"
        onChange={handleFileChange}
      />
    </div>
  );
}
